import type {AnalyticsData, AnalyticsWrapper} from './analytics-wrapper';
import {
  projectSubtypeToString,
  projectTypeToString,
  sensibleTimeValue,
  truncate,
} from './helper';
import type {CustomDimension, ProjectAnalyticsInfo} from './types';

// TODO: import types when BE proto is ready
// import {
//   AnalyticsAccountTypeDetails,
//   AnalyticsNetworkBoxInfo,
//   UserDetails,
// } from 'google3/isp/fiber/customer/portal2/proto/common.proto';

/**
 * These constants match previous dart version.
 * The numbers are divided by two because units are bytes in
 * dart analytics service, while units here are characters.
 */
const MAX_EVENT_CATEGORY_LENGTH = 75;
const MAX_EVENT_ACTION_LENGTH = 250;
const MAX_EVENT_LABEL_LENGTH = 250;
const MAX_EXCEPTION_DESC_LENGTH = 75;
const MAX_TIMING_CATEGORY_LENGTH = 75;
const MAX_TIMING_NAME_LENGTH = 250;
const MAX_TIMING_LABEL_LENGTH = 250;

const MAX_CUSTOM_DIMENSION_SIZE = 75;

const EMPTY_FIELD = '';

const NO_FIBERHOOD = 'no-fiberhood';
const NO_PROJECT = 'no-project';

/** Analytics tracking tag of BYOR modal/plugin. */
export const BYOR_MODAL = 'byor_modal/';

/**
 * Unknown region label.
 */
export const UNKNOWN_REGION = 'unknown';

/**
 * Interface for a mendel experiment.
 */
export interface MendelExperiment {
  readonly id: string;
  readonly variation: string;
}

/**
 * Global Google Analytics Service.
 */
export class AnalyticsService {
  constructor(private readonly wrapper: AnalyticsWrapper) {}

  /**
   * Sets the current field value of given field name.
   */
  setField(fieldName: string, value: unknown): void {
    this.wrapper.setField(fieldName, value);
  }

  // TODO: implement setAccountTypeDetails when BE proto is ready
  // setAccountTypeDetails(accountTypeDetail: AnalyticsAccountTypeDetails) {
  //   const details: {[key in string]: unknown} = {
  //     accountType: accountTypeDetail.getAccountType(),
  //   };
  //   if (accountTypeDetail.hasResidentialDetails()) {
  //     const residentialDetails = accountTypeDetail.getResidentialDetails()!;
  //     details['residentialDetails'] = {
  //       isProject: residentialDetails.getIsProject(),
  //       isFiberInsideProject: residentialDetails.getIsFiberInsideProject(),
  //       isAffordableHousingProject:
  //         residentialDetails.getIsAffordableHousingProject(),
  //       fiberInsideType: residentialDetails.getFiberInsideType(),
  //     };
  //   }
  //   if (accountTypeDetail.hasCommercialDetails()) {
  //     const commercialDetails = accountTypeDetail.getCommercialDetails()!;
  //     details['commercialDetails'] = {
  //       isSmb: commercialDetails.getIsSmb(),
  //       isLegacySmb: commercialDetails.getIsLegacySmb(),
  //       isPremiumSmb: commercialDetails.getIsPremiumSmb(),
  //     };
  //   }
  //   this.setField('accountTypeDetails', details);
  // }

  // TODO: implement setNetworkBoxInfo when BE proto is ready
  // setNetworkBoxInfo(networkBoxInfo: AnalyticsNetworkBoxInfo) {
  //   const info = {
  //     model: networkBoxInfo.getModel(),
  //     type: networkBoxInfo.getType(),
  //   };
  //   this.setField('networkBoxInfo', info);
  // }

  /**
   * Pushes the given data to GTM together with the current fields including app
   * name.
   */
  push(data: AnalyticsData): Promise<void> {
    return this.wrapper.push(data);
  }

  /**
   * Sends the event with data to GTM together with the current fields including
   * app name.
   */
  sendHit(event: string, data: AnalyticsData): Promise<void> {
    return this.wrapper.sendHit(event, data);
  }

  /**
   * Sends a page view hit.
   *
   * @return a promise which resolves after the hit is sent.
   */
  sendPageView(path: string, additionalData?: AnalyticsData): Promise<void> {
    const data: AnalyticsData = {};
    if (path && !path.startsWith('/')) {
      path = '/' + path;
    }
    if (path) {
      data['pageName'] = path;
    }

    if (additionalData) {
      Object.assign(data, additionalData);
    }

    return this.sendHit('trackPageview', data);
  }

  /**
   * Sends an event hit.
   *
   * @param category is typically the object that was interacted with (e.g.
   * button)
   * @param action is typically the type of interaction (e.g. click)
   * @param label is useful for categorizing events (e.g. nav buttons)
   * @param additionalData is useful for extending Analytics event with custom
   *     data
   *
   * @return a promise which resolves after the hit is sent.
   */
  sendEvent(
    category: string,
    action: string,
    label?: string,
    additionalData?: AnalyticsData,
    event = 'trackEvent',
  ): Promise<void> {
    if (!category) {
      throw new Error('Analytics category must not be null or empty.');
    }
    if (!action) {
      throw new Error('Analytics action must not be null or empty.');
    }

    const data: AnalyticsData = {
      eventCategory: truncate(category, MAX_EVENT_CATEGORY_LENGTH),
      eventAction: truncate(action, MAX_EVENT_ACTION_LENGTH),
    };

    if (label) {
      data['eventLabel'] = truncate(label, MAX_EVENT_LABEL_LENGTH);
    } else {
      data['eventLabel'] = EMPTY_FIELD;
    }

    if (additionalData) {
      Object.assign(data, additionalData);
    }

    return this.sendHit(event, data);
  }

  /**
   * Sends experiments tracking to Google Tag Manager.
   */
  sendExperiment(id: string, variation: string): Promise<void> {
    const analyticsData: AnalyticsData = {};
    analyticsData['event'] = 'trackExperiment';
    analyticsData['experiment'] = {
      id: id,
      variation: variation,
    };
    return this.push(analyticsData);
  }

  /**
   * Sends a event with optional data to Google Tag Manager
   */
  sendEventFor(
    eventName: string,
    additionalData?: AnalyticsData,
  ): Promise<void> {
    let analyticsData: AnalyticsData = {event: eventName};
    if (additionalData) {
      analyticsData = {...analyticsData, ...additionalData};
    }
    return this.push(analyticsData);
  }

  /**
   * Sends experiments tracking all together to Google Tag Manager.
   */
  sendExperiments(mendelExperimentList: MendelExperiment[]): Promise<void> {
    const analyticsData: AnalyticsData = {};
    let experimentList = '';
    for (const experiment of mendelExperimentList) {
      experimentList += experiment.id + '-' + experiment.variation + ',';
    }
    analyticsData['event'] = 'trackExperiments';
    analyticsData['experimentList'] = experimentList;
    return this.push(analyticsData);
  }

  /**
   * Helper to truncate any field values if they are too long.
   */
  static truncateIfNeeded(value: string, maxBytes: number): string {
    if (!value) {
      return value;
    }

    // Truncate value if needed
    const maxLength = maxBytes / 2; // 2 bytes per char
    if (value.length > maxLength) {
      value = value.substring(0, maxLength);
    }

    return value;
  }

  /**
   * Sends a timing hit.
   * @param category is useful for categorizing timings (eg. 'page loads').
   * @param name is the name of the timed event (e.g. 'loaded page X').
   * @param value is the elapsed time in milliseconds.
   * @param label is a string that can be used to add flexibility in visualizing
   * user timings in the reports (e.g. 'Google CDN')
   *
   * @return a promise which resolves after the hit is sent.
   */
  sendTiming(
    category: string,
    name: string,
    value: number,
    label?: string,
  ): Promise<void> {
    if (!category) {
      throw new Error('Analytics category must not be null or empty.');
    }
    if (!name) {
      throw new Error('Analytics name must not be null or empty.');
    }

    const timing: AnalyticsData = {
      name: truncate(name, MAX_TIMING_NAME_LENGTH),
      category: truncate(category, MAX_TIMING_CATEGORY_LENGTH),
      value: sensibleTimeValue(value),
    };

    if (label) {
      timing['label'] = truncate(label, MAX_TIMING_LABEL_LENGTH);
    }

    return this.sendHit('trackTiming', {timing: timing});
  }

  /**
   * Sends the details of an exception.
   *
   * @param description is a description of the exception.
   * @param isFatal indicates whether the exception was fatal. true indicates
   * fatal.
   *
   * @return a promise which resolves after the hit is sent.
   */
  sendException(
    description: string,
    extra: object | undefined = undefined,
    isFatal = false,
  ): Promise<void> {
    if (!description) {
      throw new Error('Analytics description must not be null or empty.');
    }

    const exceptionEvent: AnalyticsData & {
      exception: {[index: string]: unknown};
    } = {
      event: 'trackException',
      exception: {
        description: truncate(description, MAX_EXCEPTION_DESC_LENGTH),
        fatal: isFatal,
      },
    };

    if (!!extra) {
      exceptionEvent.exception.extra = extra;
    }

    return this.push(exceptionEvent);
  }

  /// Setters that are commonly used.
  set region(regionId: string) {
    if (!regionId) {
      regionId = UNKNOWN_REGION;
    }
    this.setCustomDimension('region', regionId);
  }

  set fiberhood(fiberhoodId: string) {
    if (!fiberhoodId) {
      fiberhoodId = NO_FIBERHOOD;
    }
    this.setCustomDimension('fiberhood', fiberhoodId);
  }

  set plan(planName: string) {
    this.setCustomDimension('plan', planName);
  }

  set installed(isInstalled: boolean) {
    this.setCustomDimension('installed', `${!!isInstalled}`);
  }

  set connectionStatus(status: string) {
    this.setCustomDimension('connectionStatus', status);
  }

  set deviceType(type: string) {
    this.setCustomDimension('deviceType', type);
  }

  set pageViewLinkType(type: string) {
    this.setCustomDimension('pageViewLinkType', type);
  }

  set inBand(isInBand: boolean) {
    this.setCustomDimension('inBand', `${!!isInBand}`);
  }

  set digitalInclusionEligible(isElligibal: boolean) {
    this.setCustomDimension('digitalInclusionEligible', `${!!isElligibal}`);
  }

  set pageName(pageName: string) {
    this.setCustomDimension('pageName', pageName);
  }

  set pageType(pageType: string) {
    this.setCustomDimension('pageType', pageType);
  }

  set internalUser(value: boolean) {
    this.setField('internalUser', value);
  }

  protected setCustomDimension(
    customDimension: CustomDimension,
    value: string,
  ): void {
    this.setField(customDimension, truncate(value, MAX_CUSTOM_DIMENSION_SIZE));
  }

  // TODO: implement set user when BE proto is ready
  // set user(userDetails: UserDetails) {
  //   const user = {
  //     signedIn: `${!!userDetails.getIsLoggedIn()}`,
  //     hasAccount: `${!!userDetails.getHasAccount()}`,
  //     browserTier: browserTierToString(
  //       userDetails.getBrowserTier_legacyNullable()
  //     ),
  //   };

  //   this.setField('user', user);
  // }

  set project(projectAnalyticsInfo: ProjectAnalyticsInfo | null) {
    if (projectAnalyticsInfo) {
      const project = {
        type: projectTypeToString(projectAnalyticsInfo.projectType),
        subtype: projectSubtypeToString(projectAnalyticsInfo.projectSubType),
      };
      this.setField('project', project);
    } else {
      const project = {
        type: NO_PROJECT,
        subtype: NO_PROJECT,
      };
      this.setField('project', project);
    }
  }

  /** Should be overridden in sub classes for specific apps. */
  getAppName(): string {
    return '';
  }

  setAppNameField(): void {
    if (this.getAppName()) {
      this.wrapper.setField('appName', this.getAppName());
    }
  }
  /// End Setters that are commonly used.
}
